import React, { useEffect, useState } from 'react';

import { Box, Button, DialogActions, DialogContent, Stack, Typography } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import Spinner from '~/components/Common/Spinner';

import { STEP_ADDITIONAL_INFO, STEP_PAYMENT_SCHEDULE_COMPARISON, STEP_SUMMARY } from '~/consts/refund';

import {
  PaymentScheduleAdjustResponse,
  PaymentScheduleDetail,
  PaymentScheduleItem,
  generateNewPaymentSchedule,
} from '~/services/PaymentsService';
import { formatDateSlashes } from '~/services/TimeService';

import currencyFormatter from '~/utils/currencyFormatter';

interface ExtendedPaymentScheduleDetail extends PaymentScheduleDetail {
  adjustment_amount?: number;
  charge_name?: string;
  charge_date?: string;
}

interface Props {
  orderId: string;
  refundAmount: number;
  currencyCode: string;
  item: PaymentScheduleItem;
  brand: string;
  stepHandler: (validate: any) => void;
  setBackStep: (step: any) => void;
  setCalculatedAmounts: (refundAmount: number, adjustmentAmount: number, adjustSchedule: boolean) => void;
  shouldSetBackStepOnMount?: boolean; // Set to false for new modal (AccommodationRefundV2)
}

function adjustedScheduleMapper(paymentSchedule: ExtendedPaymentScheduleDetail) {
  const currentAmount = paymentSchedule.total_amount;
  const adjustmentAmount = paymentSchedule.adjustment_amount || 0;
  const newAmount = currentAmount + adjustmentAmount;

  return {
    id: paymentSchedule.id,
    chargeName: paymentSchedule.charge_name,
    currentAmount: currentAmount,
    newAmount: newAmount,
    chargeDate: paymentSchedule.charge_date,
  };
}

export default function PaymentScheduleComparison({
  orderId,
  refundAmount,
  currencyCode,
  item,
  brand,
  stepHandler,
  setBackStep,
  setCalculatedAmounts,
  shouldSetBackStepOnMount = true, // Default to true for backward compatibility with old modal
}: Props) {
  const [newSchedule, setNewSchedule] = useState<PaymentScheduleAdjustResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Only set back step on mount if we're in the old modal context (NewModal)
    // In the old modal, setBackStep sets step_back state for navigation
    // In the new modal (AccommodationRefundV2), setBackStep directly sets the step
    // and calling it on mount would reset the step incorrectly
    if (shouldSetBackStepOnMount) {
      setBackStep(STEP_ADDITIONAL_INFO);
    }
  }, [setBackStep, shouldSetBackStepOnMount]);

  useEffect(() => {
    const fetchSchedules = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const newScheduleResponse = await generateNewPaymentSchedule(
          orderId,
          refundAmount,
          { ...item, currency_code: currencyCode },
          brand,
        );
        setNewSchedule(newScheduleResponse);
      } catch (err) {
        setError(err.message || 'Failed to fetch payment schedules');
      } finally {
        setIsLoading(false);
      }
    };

    fetchSchedules();
  }, [orderId, refundAmount, item, brand, currencyCode]);

  if (isLoading) {
    return (
      <DialogContent>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
          <Spinner />
        </Box>
      </DialogContent>
    );
  }

  if (error) {
    return (
      <DialogContent>
        <Typography color="error" textAlign="center">
          {error}
        </Typography>
      </DialogContent>
    );
  }

  const adjustedScheduleRows =
    newSchedule?.result?.adjusted_payment_schedule?.charges?.map(adjustedScheduleMapper) || [];

  const scheduleData = newSchedule?.result?.adjusted_payment_schedule?.summary
    ? {
        refund_amount: newSchedule.result.adjusted_payment_schedule.summary.refund_amount,
        schedule_amount: newSchedule.result.adjusted_payment_schedule.summary.schedule_amount,
      }
    : undefined;

  const columns: Array<GridColDef> = [
    {
      field: 'chargeName',
      headerName: 'Charge Name',
      width: 150,
    },
    {
      field: 'currentAmount',
      headerName: 'Current Amount',
      width: 140,
      valueFormatter: (value) => currencyFormatter(currencyCode, value || 0, 2),
    },
    {
      field: 'newAmount',
      headerName: 'New Amount',
      width: 140,
      valueFormatter: (value) => currencyFormatter(currencyCode, value || 0, 2),
    },
    {
      field: 'chargeDate',
      headerName: 'Date',
      width: 120,
      valueFormatter: (value) => (value ? formatDateSlashes(new Date(value)) : '-'),
    },
  ];

  return (
    <>
      <DialogContent dividers>
        <Stack spacing={3}>
          <Typography variant="h6" fontWeight="bold">
            Schedule Charges
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Review the adjusted payment schedule after applying a partial refund of{' '}
            {currencyFormatter(currencyCode, refundAmount, 2)}.
            <br />
            The refund amount will be adjusted from the last charge and then work backwards through the remaining
            charges.
          </Typography>

          <Box>
            <Box height={400}>
              <DataGrid rows={adjustedScheduleRows} columns={columns} disableRowSelectionOnClick hideFooter />
            </Box>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        {!shouldSetBackStepOnMount && (
          <Button onClick={() => setBackStep(STEP_PAYMENT_SCHEDULE_COMPARISON)}>Back</Button>
        )}
        <Button
          onClick={() => {
            stepHandler((error, next) => {
              if (scheduleData) {
                const cashRefundAmount = Math.abs(scheduleData.refund_amount || 0);
                const totalAdjustmentAmount = Math.abs(scheduleData.schedule_amount || 0) + cashRefundAmount;
                setCalculatedAmounts(cashRefundAmount, totalAdjustmentAmount, true);
              }
              // Only set back step in old modal context (NewModal)
              // In new modal (AccommodationRefundV2), navigation is handled by next() directly
              if (shouldSetBackStepOnMount) {
                setBackStep(STEP_PAYMENT_SCHEDULE_COMPARISON);
              }
              next(STEP_SUMMARY);
            });
          }}
          variant="contained"
        >
          Continue
        </Button>
      </DialogActions>
    </>
  );
}
